import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { globalSearch } from '@/lib/api';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { Badge } from '@/components/ui/badge';
import {
  Building2,
  Users,
  FileText,
  ClipboardList,
  FolderOpen,
  Search,
  ArrowRight
} from 'lucide-react';

const typeIcons = {
  client: Building2,
  worker: Users,
  invoice: FileText,
  order: ClipboardList,
  document: FolderOpen,
};

const typeLabels = {
  client: 'Client',
  worker: 'Worker',
  invoice: 'Invoice',
  order: 'Order',
  document: 'Document',
};

const typeColors = {
  client: 'bg-blue-100 text-blue-700',
  worker: 'bg-purple-100 text-purple-700',
  invoice: 'bg-emerald-100 text-emerald-700',
  order: 'bg-amber-100 text-amber-700',
  document: 'bg-slate-100 text-slate-700',
};

export default function GlobalSearch({ open, onOpenChange }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Debounced search
  useEffect(() => {
    if (!query || query.length < 2) {
      setResults([]);
      return;
    }

    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await globalSearch(query);
        setResults(res.data.results || []);
      } catch (error) {
        console.error('Search failed:', error);
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [query]);

  const handleSelect = useCallback((item) => {
    onOpenChange(false);
    setQuery('');
    navigate(item.url);
  }, [navigate, onOpenChange]);

  // Keyboard shortcut
  useEffect(() => {
    const down = (e) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        onOpenChange(!open);
      }
    };

    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, [open, onOpenChange]);

  // Group results by type
  const groupedResults = results.reduce((acc, item) => {
    if (!acc[item.type]) acc[item.type] = [];
    acc[item.type].push(item);
    return acc;
  }, {});

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput
        placeholder="Search clients, invoices, orders..."
        value={query}
        onValueChange={setQuery}
        data-testid="global-search-input"
      />
      <CommandList>
        {loading && (
          <div className="py-6 text-center text-sm text-muted-foreground">
            <Search className="w-4 h-4 animate-pulse mx-auto mb-2" />
            Searching...
          </div>
        )}
        
        {!loading && query.length >= 2 && results.length === 0 && (
          <CommandEmpty>No results found for "{query}"</CommandEmpty>
        )}

        {!loading && query.length < 2 && (
          <div className="py-6 text-center text-sm text-muted-foreground">
            Type at least 2 characters to search...
          </div>
        )}

        {Object.entries(groupedResults).map(([type, items]) => {
          const Icon = typeIcons[type] || Search;
          return (
            <CommandGroup key={type} heading={typeLabels[type] + 's'}>
              {items.map((item) => (
                <CommandItem
                  key={`${item.type}-${item.id}`}
                  value={`${item.title} ${item.subtitle}`}
                  onSelect={() => handleSelect(item)}
                  className="flex items-center gap-3 py-3 cursor-pointer"
                  data-testid={`search-result-${item.type}`}
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${typeColors[type]}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">{item.title}</div>
                    <div className="text-xs text-muted-foreground truncate">{item.subtitle}</div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-muted-foreground" />
                </CommandItem>
              ))}
            </CommandGroup>
          );
        })}

        {results.length > 0 && <CommandSeparator />}

        <CommandGroup heading="Quick Actions">
          <CommandItem onSelect={() => { onOpenChange(false); navigate('/clients/new'); }}>
            <Building2 className="w-4 h-4 mr-2" />
            Create New Client
          </CommandItem>
          <CommandItem onSelect={() => { onOpenChange(false); navigate('/service-orders/new'); }}>
            <ClipboardList className="w-4 h-4 mr-2" />
            Create New Order
          </CommandItem>
          <CommandItem onSelect={() => { onOpenChange(false); navigate('/invoices/new'); }}>
            <FileText className="w-4 h-4 mr-2" />
            Create New Invoice
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}
